import { useState, useRef, useEffect, useCallback } from 'react';
import { DEMO_DATASETS, DemoDataset } from '@freight-cbm/shared-logic';
import { loadDemo } from '@/store/loadDemo';
import { useClickOutside } from '@/hooks/useClickOutside';

interface DemoPickerProps {
  onLoad: () => void;
}

const TAG_COLORS: Record<string, { bg: string; text: string }> = {
  Air: { bg: 'var(--color-accent-light)', text: 'var(--color-accent)' },
  Ocean: { bg: 'var(--color-info-light)', text: 'var(--color-info)' },
  Courier: { bg: 'var(--color-warning-light)', text: 'var(--color-warning)' },
};

export function DemoPicker({ onLoad }: DemoPickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => setOpen(false), []);
  useClickOutside(ref, closeMenu);

  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const handleSelect = (demo: DemoDataset) => {
    loadDemo(demo);
    setOpen(false);
    onLoad();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-sm demo-picker-trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Load demo dataset"
      >
        Demos
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          style={{
            marginLeft: 'var(--space-1)',
            transition: 'transform var(--transition-fast)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          aria-hidden="true"
        >
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          className="demo-picker-dropdown"
          role="menu"
          aria-label="Demo datasets"
        >
          <div className="demo-picker-header">
            Load a sample shipment to explore the calculator
          </div>
          {DEMO_DATASETS.map((demo) => {
            const colors = TAG_COLORS[demo.tag] ?? { bg: 'var(--color-bg-tertiary)', text: 'var(--color-text-secondary)' };
            return (
              <button
                type="button"
                key={demo.id}
                className="demo-picker-item"
                role="menuitem"
                onClick={() => handleSelect(demo)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                  <span className="demo-picker-tag" style={{ background: colors.bg, color: colors.text }}>
                    {demo.tag}
                  </span>
                  <span style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)' }}>
                    {demo.name}
                  </span>
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', lineHeight: 'var(--line-height-normal)', textAlign: 'left' }}>
                  {demo.description}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
                  {demo.packages.length} line{demo.packages.length !== 1 ? 's' : ''} · {demo.packages.reduce((s, p) => s + p.quantity, 0)} pkg{demo.packages.reduce((s, p) => s + p.quantity, 0) !== 1 ? 's' : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
